/**
 * @module memory
 * @description Implements an in-memory logger transport for tests, Storybook
 * fixtures, and offline prototypes that need to assert structured log output.
 */

import type { LoggerAdapter, LogLevel, LogMeta } from "./interface";

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/** Structured entry captured by the memory logger transport. */
export interface MemoryLogEntry {
  /** ISO 8601 timestamp captured when the entry was written */
  timestamp: string;
  /** Entry severity level */
  level: LogLevel;
  /** Human-readable log message */
  message: string;
  /** Merged default and per-entry metadata */
  meta?: LogMeta;
}

/** Configuration for the in-memory logger transport. */
export interface MemoryLoggerOptions {
  /** Minimum log level to capture. Defaults to debug. */
  minLevel?: LogLevel;
  /** Existing entry buffer to append to, useful for composing test fixtures. */
  entries?: MemoryLogEntry[];
  /** Clock hook for deterministic tests. */
  now?: () => Date;
}

/**
 * Logger adapter with test-friendly access to captured entries.
 */
export interface MemoryLoggerAdapter extends LoggerAdapter {
  /** Frozen cloned snapshot of captured log entries. */
  readonly entries: readonly Readonly<MemoryLogEntry>[];
  /** Snapshot captured entries so assertions cannot mutate the live buffer. */
  getEntries(): MemoryLogEntry[];
  /** Clear captured entries from the shared buffer. */
  clear(): void;
  /** Create a child memory logger that appends to the same captured buffer. */
  child(defaultMeta: LogMeta): Promise<MemoryLoggerAdapter>;
}

/**
 * Creates an in-memory structured logger for test and local review flows.
 *
 * @param options - Capture behavior, injected buffer, and clock configuration
 * @returns A memory logger with captured-entry helpers and child metadata support
 */
export function createMemoryLoggerAdapter(
  options?: MemoryLoggerOptions,
): MemoryLoggerAdapter {
  const minLevel = options?.minLevel ?? "debug";
  const entries = options?.entries ?? [];
  const now = options?.now ?? (() => new Date());

  function shouldCapture(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[minLevel];
  }

  function cloneEntry(entry: MemoryLogEntry): MemoryLogEntry {
    return {
      ...entry,
      meta: entry.meta ? { ...entry.meta } : undefined,
    };
  }

  function freezeEntry(entry: MemoryLogEntry): Readonly<MemoryLogEntry> {
    const snapshot = cloneEntry(entry);
    if (snapshot.meta) {
      Object.freeze(snapshot.meta);
    }
    return Object.freeze(snapshot);
  }

  function createLogger(defaultMeta?: LogMeta): MemoryLoggerAdapter {
    async function log(
      level: LogLevel,
      message: string,
      meta?: LogMeta,
    ): Promise<void> {
      if (!shouldCapture(level)) return;

      const mergedMeta: LogMeta = {
        ...(defaultMeta ?? {}),
        ...(meta ?? {}),
      };
      const entry: MemoryLogEntry = {
        timestamp: now().toISOString(),
        level,
        message,
      };

      if (Object.keys(mergedMeta).length > 0) {
        entry.meta = mergedMeta;
      }
      entries.push(entry);
    }

    return {
      get entries() {
        return Object.freeze(entries.map(freezeEntry));
      },
      getEntries: () => entries.map(cloneEntry),
      clear: () => {
        entries.length = 0;
      },
      debug: (message, meta) => log("debug", message, meta),
      info: (message, meta) => log("info", message, meta),
      warn: (message, meta) => log("warn", message, meta),
      error: (message, meta) => log("error", message, meta),
      child: async (childMeta) =>
        createLogger({ ...(defaultMeta ?? {}), ...childMeta }),
    };
  }

  return createLogger();
}
